/**
 * PortalBookingDetail - Customer-facing booking detail page
 * Shows status, vehicle, timeline, estimate, approval actions, and photo upload
 */

import { useState, useEffect, useCallback } from 'react';
import { CancelConfirmModal } from '../../components/modals/CancelConfirm';
import { RescheduleModal } from '../../components/modals/Reschedule';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Clock, Car, Calendar, FileText, Camera,
  CheckCircle, XCircle, Loader2, AlertCircle, Upload,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getApiBase } from '../../lib/apiBase';

const API_BASE = getApiBase();

interface BookingDetail {
  id: string;
  scheduledAt: number;
  status: string;
  bookingReference?: string;
  serviceType?: string;
  notes?: string;
  bookingType?: string;
  canCancel: boolean;
  canReschedule: boolean;
  jobId?: string;
  vehicle: {
    id: string;
    registration: string;
    make: string;
    model: string;
    year?: number;
    color?: string;
  } | null;
  customer?: {
    name: string;
    email: string;
  };
  estimateAmount?: number | null;
  estimateNotes?: string | null;
  approvalStatus?: string | null;
  timeline?: TimelineEvent[];
  images?: BookingImage[];
}

interface TimelineEvent {
  id: string;
  type: string;
  description: string;
  createdAt: number;
  metadata?: Record<string, unknown>;
}

interface BookingImage {
  id: string;
  originalFilename?: string;
  uploadedAt?: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: typeof Clock }> = {
  PENDING_CONFIRMATION: { label: 'Pendiente de Confirmación', color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200', icon: Clock },
  UNVERIFIED: { label: 'Pendiente de Verificación', color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200', icon: Clock },
  CONFIRMED: { label: 'Confirmada', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: CheckCircle },
  AWAITING_DROPOFF: { label: 'Esperando Entrega', color: 'text-indigo-700', bg: 'bg-indigo-50 border-indigo-200', icon: Car },
  DROPOFF_COMPLETE: { label: 'Vehículo Recibido', color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: CheckCircle },
  CHECKED_IN: { label: 'Recibido en Taller', color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: CheckCircle },
  DIAGNOSIS: { label: 'En Diagnóstico', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', icon: FileText },
  QUOTE_PENDING: { label: 'Presupuesto Pendiente', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: FileText },
  APPROVED: { label: 'Aprobado', color: 'text-green-700', bg: 'bg-green-50 border-green-200', icon: CheckCircle },
  IN_PROGRESS: { label: 'En Proceso', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: Clock },
  QC_CHECK: { label: 'Control de Calidad', color: 'text-teal-700', bg: 'bg-teal-50 border-teal-200', icon: CheckCircle },
  READY_FOR_PICKUP: { label: 'Listo para Recoger', color: 'text-green-700', bg: 'bg-green-50 border-green-200', icon: CheckCircle },
  COMPLETED: { label: 'Completado', color: 'text-gray-700', bg: 'bg-gray-50 border-gray-200', icon: CheckCircle },
  CANCELLED: { label: 'Cancelado', color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: XCircle },
};

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] || { label: status, color: 'text-gray-700', bg: 'bg-gray-50 border-gray-200', icon: Clock };
}

export function PortalBookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  const sessionId = localStorage.getItem('customer_session');

  const fetchBooking = useCallback(async () => {
    if (!sessionId || !id) {
      setError('Sesión no encontrada');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/customer-portal/bookings/${id}`, {
        headers: { 'X-Customer-Session': sessionId },
      });
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem('customer_session');
          navigate('/portal/login', { replace: true });
          return;
        }
        throw new Error('Error al cargar la cita');
      }
      const data = await res.json();
      setBooking(data.data);
    } catch {
      setError('Error al cargar los detalles de la cita');
    } finally {
      setLoading(false);
    }
  }, [sessionId, id, navigate]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !booking || !sessionId) return;

    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => formData.append('image', file));

      const res = await fetch(`${API_BASE}/api/bookings/${booking.id}/images`, {
        method: 'POST',
        headers: { 'X-Customer-Session': sessionId },
        body: formData,
      });

      if (!res.ok) throw new Error('Error al subir fotos');
      await fetchBooking();
    } catch {
      setError('Error al subir las fotos');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCancelSuccess = async () => {
    setShowCancelModal(false);
    await fetchBooking();
  };

  const handleRescheduleSuccess = async () => {
    setShowRescheduleModal(false);
    await fetchBooking();
  };

  const formatDate = (timestamp: number) => {
    const d = new Date(timestamp * 1000);
    return d.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString('es-ES', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
        <p className="text-gray-700">{error}</p>
        <Link to="/portal/bookings">
          <Button variant="outline" className="mt-4">Volver a Mis Citas</Button>
        </Link>
      </div>
    );
  }

  if (!booking) return null;

  const statusCfg = getStatusConfig(booking.status);
  const StatusIcon = statusCfg.icon;
  const isQuotePending = booking.status === 'QUOTE_PENDING';
  const showEstimate = booking.estimateAmount !== null && booking.estimateAmount !== undefined;

  return (
    <div className="p-4 pb-8 space-y-4">
      {/* Back button */}
      <Link
        to="/portal/bookings"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
      >
        <ChevronLeft className="h-4 w-4" />
        Mis Citas
      </Link>

      {/* Status banner */}
      <div className={`rounded-xl border p-4 ${statusCfg.bg}`}>
        <div className="flex items-center gap-3">
          <StatusIcon className={`h-6 w-6 ${statusCfg.color}`} />
          <div>
            <h2 className={`text-lg font-semibold ${statusCfg.color}`}>
              {statusCfg.label}
            </h2>
            {booking.bookingReference && (
              <p className="text-sm text-gray-500 font-mono">
                Ref: {booking.bookingReference}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Appointment info */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            Cita
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Fecha</span>
              <p className="font-medium capitalize">{formatDate(booking.scheduledAt)}</p>
            </div>
            <div>
              <span className="text-gray-500">Hora</span>
              <p className="font-medium">{formatTime(booking.scheduledAt)}</p>
            </div>
            {booking.serviceType && (
              <div className="col-span-2">
                <span className="text-gray-500">Servicio</span>
                <p className="font-medium">{booking.serviceType}</p>
              </div>
            )}
            {booking.notes && (
              <div className="col-span-2">
                <span className="text-gray-500">Notas</span>
                <p className="text-gray-700">{booking.notes}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vehicle info */}
      {booking.vehicle && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Car className="h-4 w-4 text-gray-400" />
              Vehículo
            </h3>
            <div className="text-sm space-y-1">
              <p className="text-xl font-mono font-bold text-gray-900">
                {booking.vehicle.registration}
              </p>
              <p className="text-gray-600">
                {booking.vehicle.make} {booking.vehicle.model}
                {booking.vehicle.year && ` (${booking.vehicle.year})`}
              </p>
              {booking.vehicle.color && (
                <p className="text-gray-500">Color: {booking.vehicle.color}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Estimate / Quote section */}
      {(showEstimate || isQuotePending) && (
        <Card className={isQuotePending ? 'border-amber-300 bg-amber-50/50' : ''}>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Presupuesto
            </h3>
            {showEstimate && (
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {((booking.estimateAmount || 0) / 100).toLocaleString('es-ES', {
                    style: 'currency',
                    currency: 'EUR',
                  })}
                </p>
                {booking.estimateNotes && (
                  <p className="text-sm text-gray-600 mt-1">{booking.estimateNotes}</p>
                )}
                {booking.approvalStatus && (
                  <div className={`inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs font-medium ${
                    booking.approvalStatus === 'approved'
                      ? 'bg-green-100 text-green-700'
                      : booking.approvalStatus === 'rejected'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {booking.approvalStatus === 'approved' ? (
                      <><CheckCircle className="h-3 w-3" /> Aprobado</>
                    ) : booking.approvalStatus === 'rejected' ? (
                      <><XCircle className="h-3 w-3" /> Rechazado</>
                    ) : (
                      <><Clock className="h-3 w-3" /> Pendiente de aprobación</>
                    )}
                  </div>
                )}
              </div>
            )}
            {isQuotePending && booking.approvalStatus !== 'approved' && booking.approvalStatus !== 'rejected' && (
              <div className="flex gap-3 pt-2">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={false}
                  onClick={async () => {
                    // Approval via the public webhook mechanism
                    // For now, we show the info — actual approve/reject uses email links
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Aprobar
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                  disabled={false}
                  onClick={async () => {
                    // Rejection via the public webhook mechanism
                  }}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Rechazar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Photo upload section */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <Camera className="h-4 w-4 text-gray-400" />
            Fotos del Vehículo
          </h3>

          {/* Existing photos */}
          {booking.images && booking.images.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {booking.images.map((img) => (
                <a
                  key={img.id}
                  href={`${API_BASE}/api/bookings/${booking.id}/images/${img.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="relative aspect-square rounded-lg overflow-hidden border bg-gray-100 group"
                >
                  <img
                    src={`${API_BASE}/api/bookings/${booking.id}/images/${img.id}`}
                    alt="Foto del vehículo"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          )}

          {/* Upload button */}
          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
            <input
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              className="hidden"
              onChange={handlePhotoUpload}
              disabled={uploading}
            />
            {uploading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-sm text-blue-600">Subiendo...</span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-500">
                  Subir fotos del vehículo
                </span>
              </>
            )}
          </label>
          <p className="text-xs text-gray-400 text-center">
            Fotos de daños, estado actual o documentación
          </p>
        </CardContent>
      </Card>

      {/* Timeline */}
      {booking.timeline && booking.timeline.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-400" />
              Historial
            </h3>
            <div className="space-y-0">
              {booking.timeline.map((event, index) => (
                <div
                  key={event.id}
                  className="relative flex gap-3 pb-4 last:pb-0"
                >
                  {/* Timeline line */}
                  {index < booking.timeline!.length - 1 && (
                    <div className="absolute left-[9px] top-5 bottom-0 w-px bg-gray-200" />
                  )}
                  {/* Dot */}
                  <div className="relative z-10 mt-1.5 h-[18px] w-[18px] rounded-full border-2 border-blue-300 bg-blue-50 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-700">{event.description}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDateTime(event.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action buttons */}
      {(booking.canCancel || booking.canReschedule) && (
        <div className="flex gap-3">
          {booking.canReschedule && (
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowRescheduleModal(true)}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Reprogramar
            </Button>
          )}
          {booking.canCancel && (
            <Button
              variant="outline"
              className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => setShowCancelModal(true)}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Cancelar Cita
            </Button>
          )}
        </div>
      )}

      {showCancelModal && (
        <CancelConfirmModal
          booking={{
            id: booking.id,
            scheduledAt: new Date(booking.scheduledAt * 1000).toISOString(),
            vehicle: booking.vehicle
              ? {
                  registration: booking.vehicle.registration,
                  make: booking.vehicle.make,
                  model: booking.vehicle.model,
                }
              : null,
          }}
          onClose={() => setShowCancelModal(false)}
          onSuccess={handleCancelSuccess}
        />
      )}

      {showRescheduleModal && (
        <RescheduleModal
          booking={{
            id: booking.id,
            scheduledAt: new Date(booking.scheduledAt * 1000).toISOString(),
            vehicle: booking.vehicle
              ? {
                  registration: booking.vehicle.registration,
                  make: booking.vehicle.make,
                  model: booking.vehicle.model,
                }
              : null,
          }}
          onClose={() => setShowRescheduleModal(false)}
          onSuccess={handleRescheduleSuccess}
        />
      )}
    </div>
  );
}
